import { useState } from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Typography,
  Chip,
  Menu,
  MenuItem as MenuOption,
  Button,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { AdminBugReport, AdminTableColumn, AdminTableAction } from '../../../types/admin';
import AdminTable from '../../admin/shared/AdminTable';
import BugReportDetailModal from './BugReportDetailModal';
import { format } from 'date-fns';

interface BugReportsModerationProps {
  bugReports: AdminBugReport[];
  isLoading: boolean;
  onSearch: (query: string) => void;
  onFilterStatus: (status: string) => void;
  onFilterSeverity: (severity: string) => void;
  onFilterCategory: (category: string) => void;
  onUpdateStatus: (reportId: string, status: AdminBugReport['status']) => Promise<{ success: boolean; error?: string }>;
  onAssign: (reportId: string, assignee: string) => Promise<{ success: boolean; error?: string }>;
}

const CATEGORIES = [
  'UI/UX Issue',
  'Performance',
  'Feature Request',
  'Data Error',
  'Security Concern',
  'Mobile App',
  'Integration Issue',
  'Billing',
  'Authentication',
  'Export/Import',
];

const getSeverityColor = (severity: string): 'error' | 'warning' | 'info' | 'default' => {
  switch (severity) {
    case 'critical':
      return 'error';
    case 'high':
      return 'warning';
    case 'medium':
      return 'info';
    case 'low':
    default:
      return 'default';
  }
};

/**
 * BugReportsModeration - Bug reports queue management tab
 *
 * Features:
 * - Search and filter bug reports
 * - View report details
 * - Update status
 * - Assign reports
 * - Severity color coding
 */
export default function BugReportsModeration({
  bugReports,
  isLoading,
  onSearch,
  onFilterStatus,
  onFilterSeverity,
  onFilterCategory,
  onUpdateStatus,
  onAssign,
}: BugReportsModerationProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedReport, setSelectedReport] = useState<AdminBugReport | null>(null);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);
  const [reportForStatusChange, setReportForStatusChange] = useState<AdminBugReport | null>(null);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    onSearch(query);
  };

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status);
    onFilterStatus(status === 'all' ? '' : status);
  };

  const handleSeverityFilter = (severity: string) => {
    setSeverityFilter(severity);
    onFilterSeverity(severity === 'all' ? '' : severity);
  };

  const handleCategoryFilter = (category: string) => {
    setCategoryFilter(category);
    onFilterCategory(category === 'all' ? '' : category);
  };

  const handleViewReport = (report: AdminBugReport) => {
    setSelectedReport(report);
  };

  const handleOpenStatusMenu = (event: React.MouseEvent<HTMLElement>, report: AdminBugReport) => {
    event.stopPropagation();
    setStatusMenuAnchor(event.currentTarget);
    setReportForStatusChange(report);
  };

  const handleStatusChange = async (status: AdminBugReport['status']) => {
    if (reportForStatusChange) {
      await onUpdateStatus(reportForStatusChange.id, status);
      setStatusMenuAnchor(null);
      setReportForStatusChange(null);
    }
  };

  const handleAssign = async (report: AdminBugReport) => {
    await onAssign(report.id, 'engineering@cribcritic.com');
  };

  const columns: AdminTableColumn<AdminBugReport>[] = [
    {
      key: 'id',
      label: 'ID',
      width: 100,
      render: (report) => (
        <Typography variant="body2" fontFamily="monospace">
          #{report.id.split('_')[1]}
        </Typography>
      ),
    },
    {
      key: 'severity',
      label: 'Severity',
      width: 100,
      render: (report) => (
        <Chip
          label={report.severity.toUpperCase()}
          color={getSeverityColor(report.severity)}
          size="small"
        />
      ),
    },
    {
      key: 'subject',
      label: 'Subject',
      render: (report) => (
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {report.subject}
          </Typography>
          <Chip
            label={report.category}
            size="small"
            variant="outlined"
            sx={{ mt: 0.5 }}
          />
        </Box>
      ),
    },
    {
      key: 'user_email',
      label: 'Reporter',
      width: 200,
      render: (report) => (
        <Box>
          <Typography variant="body2">{report.user_name || 'Unknown'}</Typography>
          <Typography variant="caption" color="text.secondary">
            {report.user_email}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      width: 150,
      render: (report) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={report.status.replace('_', ' ')}
            color={
              report.status === 'resolved'
                ? 'success'
                : report.status === 'in_progress'
                ? 'warning'
                : report.status === 'closed'
                ? 'default'
                : 'info'
            }
            size="small"
          />
          <Button
            size="small"
            onClick={(e) => handleOpenStatusMenu(e, report)}
          >
            <MoreVertIcon fontSize="small" />
          </Button>
        </Box>
      ),
    },
    {
      key: 'created_at',
      label: 'Created',
      width: 120,
      render: (report) => (
        <Typography variant="body2">
          {format(new Date(report.created_at), 'MMM d, yyyy')}
        </Typography>
      ),
    },
  ];

  const actions: AdminTableAction<AdminBugReport>[] = [
    {
      label: 'View Details',
      icon: <VisibilityIcon />,
      onClick: handleViewReport,
      color: 'primary',
    },
    {
      label: 'Assign',
      icon: <AssignmentIcon />,
      onClick: handleAssign,
      disabled: (report) => !!report.assigned_to,
    },
  ];

  return (
    <Box>
      {/* Filters */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            placeholder="Search reports..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
            }}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => handleStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="new">New</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
              <MenuItem value="closed">Closed</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={2}>
          <FormControl fullWidth>
            <InputLabel>Severity</InputLabel>
            <Select
              value={severityFilter}
              label="Severity"
              onChange={(e) => handleSeverityFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="critical">Critical</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="low">Low</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryFilter}
              label="Category"
              onChange={(e) => handleCategoryFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              {CATEGORIES.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography variant="body2" color="text.secondary">
              {bugReports.length} reports
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Table */}
      <AdminTable
        columns={columns}
        data={bugReports}
        actions={actions}
        isLoading={isLoading}
      />

      {/* Report Detail Modal */}
      <BugReportDetailModal
        open={!!selectedReport}
        report={selectedReport}
        onClose={() => setSelectedReport(null)}
      />

      {/* Status Change Menu */}
      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={() => {
          setStatusMenuAnchor(null);
          setReportForStatusChange(null);
        }}
      >
        <MenuOption onClick={() => handleStatusChange('new')}>New</MenuOption>
        <MenuOption onClick={() => handleStatusChange('in_progress')}>In Progress</MenuOption>
        <MenuOption onClick={() => handleStatusChange('resolved')}>Resolved</MenuOption>
        <MenuOption onClick={() => handleStatusChange('closed')}>Closed</MenuOption>
      </Menu>
    </Box>
  );
}
